import { configureSurfaceMaterial } from './SurfaceMaterial';
import { rockyTerrain, paintTerrain } from './Terrain';
import * as THREE from 'three';
import { Planet } from './Planet';
import type { Vec3 } from '../physics/Body';
import { G, ORBIT_SCALE, VISUAL_PLANET_MULT, assetUrl } from '../config/constants';

/** Moon starts on +Z from Earth; +X is tangential to that orbit. */
export function lunarOrbitVelocity(primaryMass: number, primaryVelocity: Vec3, separation: number): Vec3 {
  return [primaryVelocity[0] + Math.sqrt(G * (primaryMass + 2.2e23) / separation), primaryVelocity[1], primaryVelocity[2]];
}

const VS = ORBIT_SCALE * VISUAL_PLANET_MULT;
const SEGMENTS = 192;

const hash = (x: number, y: number, z: number) => {
  const n = Math.sin(x * 127.1 + y * 311.7 + z * 74.9) * 43758.5453;
  return n - Math.floor(n);
};

const noise3D = (x: number, y: number, z: number) => {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = x - ix, fy = y - iy, fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const sz = fz * fz * (3 - 2 * fz);
  const v000 = hash(ix, iy, iz);
  const v100 = hash(ix + 1, iy, iz);
  const v010 = hash(ix, iy + 1, iz);
  const v110 = hash(ix + 1, iy + 1, iz);
  const v001 = hash(ix, iy, iz + 1);
  const v101 = hash(ix + 1, iy, iz + 1);
  const v011 = hash(ix, iy + 1, iz + 1);
  const v111 = hash(ix + 1, iy + 1, iz + 1);
  return lerp(lerp(lerp(v000, v100, sx), lerp(v010, v110, sx), sy),
              lerp(lerp(v001, v101, sx), lerp(v011, v111, sx), sy), sz);
};

const fbm3D = (x: number, y: number, z: number, octaves: number) => {
  let v = 0, a = 1, t = 0;
  for (let i = 0; i < octaves; i++) {
    const f = 1 << i;
    v += a * noise3D(x * f, y * f, z * f);
    t += a;
    a *= 0.5;
  }
  return v / t;
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export class Moon extends Planet {
  protected override getTerrainHeightVisual(nx: number, ny: number, nz: number): number {
    return this.visualRadius * rockyTerrain('moon', nx, ny, nz);
  }

  constructor(position: Vec3, velocity: Vec3) {
    super("moon", 2.2e23, position, velocity, 1.737e6 * 1.25);

    const visualR = this.visualRadius;

    const loader = new THREE.TextureLoader();
    const tex = loader.load(assetUrl('/textures/moon.jpg'), (t) => {
      t.colorSpace = THREE.SRGBColorSpace; t.wrapS = THREE.RepeatWrapping;
      t.anisotropy = 4;
    });

    const geom = new THREE.SphereGeometry(visualR, SEGMENTS, SEGMENTS);
    const posAttr = geom.attributes.position!;
    const vert = new THREE.Vector3();
    for (let i = 0; i < posAttr.count; i++) {
      vert.fromBufferAttribute(posAttr, i);
      const direction = vert.clone().normalize();
      const disp = this.getTerrainHeightVisual(direction.x, direction.y, direction.z);

      vert.setLength(visualR + disp);
      posAttr.setXYZ(i, vert.x, vert.y, vert.z);
    }
    posAttr.needsUpdate = true;
    geom.computeVertexNormals();

    paintTerrain(geom, 'moon', this.visualRadius);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      vertexColors: true,
      roughness: 0.95,
      metalness: 0.0,
      color: 0xffffff,
    });

    configureSurfaceMaterial(mat, 'moon', this.visualRadius);
    this.mesh = new THREE.Mesh(geom, mat);
    this.mesh.position.set(position[0] * VS, position[1] * VS, position[2] * VS);
  }
}
